import { Flex, Text } from '@chakra-ui/react'
import { Table } from '@chakra-ui/react'
import { t } from 'i18next'
import { useMemo } from 'react'
import { useUserState } from '~/atoms/UserState'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { InitialsAvatar } from '~/components/shared/InitialsAvatar'
import { QueueAgentStatus } from '~/hooks/useCallQueue'
import { useGetCustomStatuses } from '~/queries/customStatusQueries'
import { useGetPbxLineExtensions } from '~/queries/extensionQueries'
import { useGetAgentStatuses } from '~/queries/queueQueries'
import { AgentCustomStatus, Availability } from '~/types/AgentCustomStatus'

import { ExtensionPreview } from '@mangovoice/types'

import { useDashboardContext } from '../context/DashboardContext'
import AgentStatusActionsCellContent from './AgentStatusActionsCellContent'

const Thead = Table.Header
const Tbody = Table.Body
const Tr = Table.Row
const Th = Table.ColumnHeader
const Td = Table.Cell

export enum AgentStatusColumn {
  Agent = 'agent',
  Extension = 'extension',
  CurrentStatus = 'currentStatus',
  TimeInStatus = 'timeInStatus',
  StartedAt = 'startedAt',
  Actions = 'actions',
}

const tableHeader = (title: string) => {
  return (
    <Th padding={'0.75rem 1rem 0.75rem 0rem'}>
      <Text
        textTransform="none"
        fontSize={'0.75rem'}
        fontWeight={'700'}
        lineHeight={'1rem'}
        color={'fg.default'}
      >
        {title}
      </Text>
    </Th>
  )
}

const cellText = (text: string) => {
  return (
    <Text
      overflow={'hidden'}
      textOverflow={'ellipsis'}
      whiteSpace={'nowrap'}
      fontSize={'1rem'}
      fontWeight={'400'}
      lineHeight={'1.5rem'}
      color={'fg.default'}
    >
      {text}
    </Text>
  )
}

const cell = (children: React.ReactNode) => {
  return (
    <Td
      padding={'0'}
      height={'3.75rem'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'flex-start'}
      flex={'1 0 0'}
    >
      {children}
    </Td>
  )
}

const textCell = (text: string) => {
  return cell(cellText(text))
}

const agentCell = (firstName: string, lastName: string) => {
  return cell(
    <Flex alignItems={'center'} gap={'0.5rem'} alignSelf={'stretch'}>
      <InitialsAvatar firstName={firstName} lastName={lastName} size="sm" />
      {cellText(`${firstName} ${lastName}`)}
    </Flex>,
  )
}

const extensionCell = (extension: string) => {
  return cell(
    <ExtensionBadge
      variant={ExtensionBadgeVariant.Gray}
      extension={extension}
    />,
  )
}

const currentStatusCell = (loggedIn: boolean, availability?: Availability) => {
  return cell(
    <Flex alignItems={'center'} gap={'0.5rem'} alignSelf={'stretch'}>
      {loggedIn ? (
        <CircleBadge variant={CircleBadgeVariant.Green} />
      ) : availability === 'Away' ? (
        <CircleBadge variant={CircleBadgeVariant.Red} />
      ) : availability ? (
        <CircleBadge variant={CircleBadgeVariant.Blue} />
      ) : (
        <CircleBadge variant={CircleBadgeVariant.Gray} />
      )}
      {cellText(
        loggedIn
          ? t('agentStatusTable.status.inQueue')
          : !availability
            ? t('agentStatusTable.status.offline')
            : availability,
      )}
    </Flex>,
  )
}

const actionsCell = (
  customStatuses: AgentCustomStatus[],
  selectedValue: QueueAgentStatus | string,
  extension?: ExtensionPreview,
  onStatusUpdated?: () => void,
) => {
  return cell(
    <AgentStatusActionsCellContent
      customStatuses={customStatuses}
      selectedValue={selectedValue}
      extension={extension}
      onStatusUpdated={onStatusUpdated}
    />,
  )
}

const formatTimeInStatus = (timestamp: string): string => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60),
  )

  if (diffInMinutes < 1) {
    return 'Just Now'
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} mins`
  } else {
    const hours = Math.floor(diffInMinutes / 60)
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
}

const formatStartTime = (timestamp: string): string => {
  return new Date(timestamp)
    .toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase()
}

const AgentStatusTable = () => {
  const { pbxId } = useDashboardContext()
  const { pbxUsers } = useUserState()

  const { data: customStatuses } = useGetCustomStatuses(pbxId)
  const { data: agentStatuses, refetch: refetchAgentStatuses } =
    useGetAgentStatuses(pbxId)
  const { data: extensions } = useGetPbxLineExtensions({
    pbxId: pbxId ?? '',
    options: { gcTime: 60 * 60 * 1000 },
  })

  const mappedAgentStatuses = useMemo(() => {
    return agentStatuses?.results?.map((agentStatus) => {
      const extension = extensions?.results?.find(
        (extension) => extension.uuid === agentStatus.extension,
      )
      const user = pbxUsers?.find((user) => user.id === agentStatus.user_id)
      const firstName =
        agentStatus.user_first_name ?? user?.full_name?.split(' ')[0] ?? ''
      const lastName =
        agentStatus.user_last_name ??
        user?.full_name?.split(' ').slice(1).join(' ') ??
        ''
      return {
        ...agentStatus,
        extension,
        user,
        firstName,
        lastName,
      }
    })
  }, [agentStatuses, extensions, pbxUsers])

  const handleStatusUpdated = () => {
    setTimeout(() => {
      refetchAgentStatuses()
    }, 750)
  }

  return (
    <Flex
      padding={'1.25rem'}
      backgroundColor={'bg.panel'}
      alignSelf={'stretch'}
      overflow="hidden"
      flexDirection="column"
    >
      <Table.Root
        variant="line"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <Thead borderBottom={'0.1rem solid var(--chakra-colors-border-subtle)'}>
          <Tr>
            {tableHeader(t('agentStatusTable.agent'))}
            {tableHeader(t('agentStatusTable.extension'))}
            {tableHeader(t('agentStatusTable.currentStatus'))}
            {tableHeader(t('agentStatusTable.timeInStatus'))}
            {tableHeader(t('agentStatusTable.startedAt'))}
            {tableHeader(t('agentStatusTable.actions'))}
          </Tr>
        </Thead>
      </Table.Root>
      <Flex overflowY="auto" flex="1">
        <Table.Root
          variant="line"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <Tbody>
            {mappedAgentStatuses?.map((agent) => (
              <Tr
                key={agent.id}
                height={'4.75rem'}
                borderBottom={'0.1rem solid var(--chakra-colors-border-subtle)'}
                alignSelf={'stretch'}
              >
                {agentCell(agent.firstName, agent.lastName)}
                {extensionCell(agent.extension?.number ?? '')}
                {currentStatusCell(
                  agent.logged_in,
                  agent.agent_status?.availability,
                )}
                {textCell(formatTimeInStatus(agent.updated_at))}
                {textCell(formatStartTime(agent.updated_at))}
                {actionsCell(
                  customStatuses ?? [],
                  agent.agent_status?.id && !agent.logged_in
                    ? agent.agent_status.id
                    : agent.logged_in
                      ? QueueAgentStatus.InQueue
                      : QueueAgentStatus.OutOfQueue,
                  agent.extension,
                  handleStatusUpdated,
                )}
              </Tr>
            ))}
          </Tbody>
        </Table.Root>
      </Flex>
    </Flex>
  )
}

export default AgentStatusTable
